// GROUND WAYS — the device port of pipeline/surfacegen.py SurfacePainter's
// WAYS pass (WAYS-G, the module docstring there): set_ways -> prepWays,
// _path_pass -> pathTexel, _mark_pass -> markTexel, with every helper they
// call. Plain JS, PER TEXEL: each numpy raster op is a bounded loop over the
// ways-field cells / neighbour texels of one texel, with the numpy's
// arithmetic (float32 where it decides: bilinear bytes, texel centres; uint32
// noise hashes), so a GLSL fragment pass follows it line by line. The CPU
// reference (groundref.js) runs waysPassRaster.
//
// THE FIELD (world/pathfield.mjs ENC): one Uint8Array W1 x H1 x 8, the two
// RGBA8 planes interleaved per 1 m cell (P0 RGBA, P1 RGBA; cell (j, i) at
// (j W1 + i) 8, centre (i + 0.5, j + 0.5)) — the numpy (H, W, 8). Bilinear
// taps clamp at the field's edges.
// THE RASTER: a tile is painted on paint()'s HALOED raster: PW = pw + 2 m,
// PH = ph + 2 m, m = haloPx(texel), texel (i, j) centred at tile.xs[i],
// tile.zs[j] (float32, as _coords); colours sRGB 0..255 float, 3 per texel.
//
// API
//   prepWays(field, W1, H1, look, marks, world, win) -> state   (set_ways, once per world)
//       win    {x0, z0, w, h} | undefined: `field` holds only the cells [x0, x0 + w) x [z0, z0 + h)
//              of the W1 x H1 world (a window every read of the painted slots lies in: the pre-paint's,
//              APP_PREPAINT; memory scales with the slot, not the world); default the whole field
//       look   pipeline/wayslook.json; marks = world.json ways_field.marks
//              {line_m, kerb_line_m, ground?}; world = {pal: {class key:
//              {base, patch}} (the painter's tinted PALETTE), ids: {class key:
//              id}, noneId, treelineM}
//   waysTile(state, x0, z0, w_m, h_m, pw, ph) -> tile | null  (per tile: CPU uniforms;
//              null = no strip within half a cell of the tile's window: the
//              pass — marks included — is a no-op)
//   hostPass(state, tile, rgb, cmAt, elevAt) -> Float32Array PH x PW x 5  (passes 1 + 2)
//              per texel (hb, Hs r g b, fL); rgb = the raster before the pass,
//              cmAt(i, j) its class id (paint's cm_win), elevAt(i, j) its
//              elevation (f['elev'])
//   pathTexel(state, tile, i, j, rgb, hx) -> [r, g, b]  (pass 3: _path_pass)
//              rgb = the texel's colour before the pass, hx = hostPass's raster
//   markTexel(state, tile, i, j, rgb) -> [r, g, b]  (pass 3, after pathTexel: _mark_pass)
//   waysPassRaster(state, x0, z0, w_m, h_m, pw, ph, out, cmAt, elevAt) -> out
//              the three in paint()'s order over the haloed raster `out`
//              (Float32Array PH x PW x 3, after the class blend + mod), in place
//
// PASSES a GLSL port needs (beyond the per-texel ways pass):
//   per world  markCells: per 1 m mark cell (P1 code 5-7) the coarse marks'
//              (F, C r g b), the mean of 4 x 4 sub-samples at 0.25 m — a
//              sparse / 1 m RGBA32F texture (prepWays).
//   per tile   CPU uniforms (waysTile): `live` (the window holds R < 144) and,
//              texel < 1 m, per stair look byte the octave keep of the MEDIAN
//              tread pitch over the tile's texels of that byte (numpy reads one
//              ka per byte per tile).
//   pass 1     host prep, per texel: (ring_h, bare ring_h, bare, rgb ring_h,
//              rgb) at colour.host.ring_m and (ring_l, L ring_l, L) at
//              steps.luma.ring_m — 12 channels (3 RGBA32F targets).
//   pass 2     their box means, radius round(radius_m / texel) texels clipped
//              to the raster (_boxblur; separable: a horizontal then a
//              vertical pass), resolved to (hb, Hs, fL) — 5 channels.
//   pass 3     pathTexel + markTexel (fused: the marks read only their texel).
// Off the halo's outermost texel ring this is the numpy's paint exactly; on
// it the numpy's window clamps (coarse taps / mark sub-samples) differ —
// paint crops the halo.
const f32 = Math.fround;

// ---------------------------------------------------------------- the field encoding (surfacegen PATH_* / WAY_*)
const R_OFF = 128, R_SCALE = 32, G_OFF = 128, G_SCALE = 63, KIND_BITS = 4, KMASK = 15;
const A_OFF = 128, A_SCALE = 32, L_OFF = 128, L_SCALE = 64, D_OFF = 128, D_SCALE = 32, P_OFF = 128;
const CODE_BITS = 3, CMASK = 7;
const CODE = { none: 0, centre: 1, lane: 2, sleepers: 3, treads: 4, zebra: 5, xline: 6, grid: 7 };
const MARK0 = 5;                              // codes 5-7: the ground marks
const XLINE_SUB = [[1, 'give_way'], [2, 'stop']];
const TEETH_HB0 = 2;                          // P1.B >> 3 = TEETH_HB0 + n: a give-way line with n teeth
const PATH_KINDS = ['soil', 'rock', 'track']; // the trodden look
const CROSS_WL = 1.8;                         // the cross-section's scale (m)
const KIND_PAD = 1;                           // fine regime: cells past the bilinear taps
const KIND_BLEND_TEXEL = 2.0;                 // coarse regime: kind blend over one texel's tent up to this texel
const MARK_S = 4;                             // coarse marks: S x S sub-samples per cell
const SIDE_M = 4.0;                           // the contrast floor's side lattice (m; surfacegen PATH_SIDE_M)
const WORN_REACH = 0.48;                      // the worn edge band's end off the edge, wobble included (surfacegen PATH_WORN_REACH)
// the encoding + regime constants, for groundgen.js's GLSL (one source)
export const WAYS_K = { R_OFF, R_SCALE, G_OFF, G_SCALE, KIND_BITS, KMASK, A_OFF, A_SCALE, L_OFF, L_SCALE, D_OFF, D_SCALE, P_OFF,
  CODE_BITS, CMASK, CODE, MARK0, XLINE_SUB, TEETH_HB0, PATH_KINDS, CROSS_WL, KIND_PAD, KIND_BLEND_TEXEL, MARK_S, SIDE_M };

// ---------------------------------------------------------------- scalar helpers
const clamp = (v, lo, hi) => (v < lo ? lo : v > hi ? hi : v);
// Python / numpy round (half to even)
function roundEven(v) {
  const r = Math.round(v);
  return Math.abs(v % 1) === 0.5 && r % 2 !== 0 ? r - 1 : r;
}
// np.mod of floats (the divisor's sign)
function npMod(a, b) {
  let m = a % b;
  if (m !== 0 && (b < 0) !== (m < 0)) m += b;
  return m;
}
function smooth(t, lo, hi) {
  t = clamp((t - lo) / (hi - lo), 0, 1);
  return t * t * (3 - 2 * t);
}
// 1 at >= 4 texels per wavelength, 0 at <= 2
function octaveKeep(wl, texel) {
  const t = clamp((wl / texel - 2.0) / 2.0, 0.0, 1.0);
  return t * t * (3 - 2 * t);
}
// coverage of [a, b] by a box footprint w centred at x
const boxCov = (x, a, b, w) => clamp((Math.min(x + w / 2.0, b) - Math.max(x - w / 2.0, a)) / w, 0.0, 1.0);
const lum = (r, g, b) => 0.299 * r + 0.587 * g + 0.114 * b;
// sRGB 0..255 -> [linear r g b, CIE Y, L*] (D65, Rec. 709; surfacegen _lstar_y)
export function lstarY(r, g, b) {
  const lin = [r, g, b].map((v) => { const x = clamp(v, 0, 255) / 255; return x <= 0.04045 ? x / 12.92 : ((x + 0.055) / 1.055) ** 2.4; });
  const Y = 0.2126 * lin[0] + 0.7152 * lin[1] + 0.0722 * lin[2];
  return [lin, Y, Y > 216 / 24389 ? 116 * Math.cbrt(Y) - 16 : Y * (24389 / 27)];
}
// wayslook colour.contrast (surfacegen _path_floor): c = [r, g, b] with its
// noise-free reference ref (the tread / patch mean; the rut itself), the
// host's Lh and sn the side lattice's sign mean (sideMean) — a ref closer
// than CIE L* dl sets one linear scale Y(Lt) / Y(ref), Lt = Lh + dl sd: sd =
// sn where |Lc - Lh| <= dl / 2, ramping to the texel's own side (+1 lighter,
// a tie too) at |Lc - Lh| = dl; past L* 100 / below 0 the other side whole;
// c's linear rgb x it. -> c, or a new [r, g, b]
export function pathFloor(c, ref, Lh, dl, sn) {
  const [, Y, Lc] = lstarY(ref[0], ref[1], ref[2]), d = Lc - Lh;
  if (!(Math.abs(d) < dl)) return c;
  const w = clamp((Math.abs(d) - 0.5 * dl) / (0.5 * dl), 0, 1);
  let sd = sn + ((d >= 0 ? 1 : -1) - sn) * w;
  sd = Lh + dl > 100 ? -1 : Lh - dl < 0 ? 1 : sd;
  const Lt = Lh + dl * sd;
  const s = (Lt > 8 ? ((Lt + 16) / 116) ** 3 : Lt * (27 / 24389)) / Math.max(Y, 1e-6);
  return lstarY(c[0], c[1], c[2])[0].map((v) => { const x = clamp(v * s, 0, 1); return f32((x <= 0.0031308 ? x * 12.92 : 1.055 * x ** (1 / 2.4) - 0.055) * 255); });
}
// CIELAB (D65, the sRGB matrix with lstarY's Rec. 709 Y row; surfacegen _lab_of_lin / _lin_of_lab)
const XYZ = [[0.4124564, 0.3575761, 0.1804375], [0.2126, 0.7152, 0.0722], [0.0193339, 0.1191920, 0.9503041]];
const WHITE = [0.95047, 1.0, 1.08883];
function inv3(m) {
  const [[a, b, c], [d, e, f], [g, h, k]] = m, A = e * k - f * h, B = f * g - d * k, C = d * h - e * g, det = a * A + b * B + c * C;
  return [[A / det, (c * h - b * k) / det, (b * f - c * e) / det], [B / det, (a * k - c * g) / det, (c * d - a * f) / det],
    [C / det, (b * g - a * h) / det, (a * e - b * d) / det]];
}
export const XYZ_INV = inv3(XYZ);
export function labOfLin(l) {
  const f = XYZ.map((r, k) => { const t = (r[0] * l[0] + r[1] * l[1] + r[2] * l[2]) / WHITE[k]; return t > 216 / 24389 ? Math.cbrt(t) : t * (24389 / 27 / 116) + 16 / 116; });
  return [116 * f[1] - 16, 500 * (f[0] - f[1]), 200 * (f[1] - f[2])];
}
function linOfLab(L, a, b) {
  const fy = (L + 16) / 116, t = [fy + a / 500, fy, fy - b / 200].map((f, k) => (f > 6 / 29 ? f ** 3 : (f - 16 / 116) * (27 * 116 / 24389)) * WHITE[k]);
  return XYZ_INV.map((r) => clamp(r[0] * t[0] + r[1] * t[1] + r[2] * t[2], 0, 1));
}
export const labOf = (c) => labOfLin(lstarY(c[0], c[1], c[2])[0]);
// wayslook colour.contrast's earth floor (surfacegen _earth_floor): c with its
// noise-free reference ref, hosts the CIELAB references the dE is held against
// (the ring Hs, the texel's own host H), v the vegetated-host weight: ref's
// hue clamped into ec.hue, its chroma raised to c_min and along that hue to
// the larger root of every host's de_min sphere (two passes), never past
// max(C, c_max); L* kept; the (a*, b*) step x v, c's linear rgb x target / ref.
// -> c, or a new [r, g, b]
export function earthFloor(c, ref, hosts, v, ec) {
  const lr = lstarY(ref[0], ref[1], ref[2])[0], [L, a, b] = labOfLin(lr), C = Math.hypot(a, b);
  const h0 = ec.hue[0] * Math.PI / 180, h1 = ec.hue[1] * Math.PI / 180, hc = 0.5 * (h0 + h1), hh = 0.5 * (h1 - h0);
  const dh = npMod(Math.atan2(b, a) - hc + Math.PI, 2 * Math.PI) - Math.PI, hn = hc + clamp(dh, -hh, hh);
  const ch = Math.cos(hn), sh = Math.sin(hn), de2 = ec.de_min * ec.de_min;
  let C1 = Math.max(C, ec.c_min);
  for (let it = 0; it < 2; it++) {
    for (const hl of hosts) {
      const dL = L - hl[0], e2 = dL * dL + (C1 * ch - hl[1]) ** 2 + (C1 * sh - hl[2]) ** 2;
      if (!(e2 < de2)) continue;
      const pp = hl[1] * ch + hl[2] * sh, q = hl[1] * hl[1] + hl[2] * hl[2] + dL * dL - de2;
      C1 = Math.max(C1, pp + Math.sqrt(Math.max(pp * pp - q, 0)));
    }
  }
  if (!(v > 0 && (Math.abs(dh) > hh || C1 > C))) return c;
  const C2 = Math.min(C1, Math.max(C, ec.c_max));
  const tl = linOfLab(L, a + v * (C2 * ch - a), b + v * (C2 * sh - b));
  return lstarY(c[0], c[1], c[2])[0].map((x, k) => { const y = clamp(x * (tl[k] / Math.max(lr[k], 1e-6)), 0, 1); return f32((y <= 0.0031308 ? y * 12.92 : 1.055 * y ** (1 / 2.4) - 0.055) * 255); });
}
// the vegetated-host weight (surfacegen _veg_weight): the ring's CIE a* < 0, clamp(-a* / veg_a, 0, 1)
export const vegWeight = (hsLab, vegA) => clamp(-hsLab[1] / vegA, 0, 1);
// the side lattice around raster texel (i, j) (surfacegen _side_nodes): a node
// every max(SIDE_M, 2 texels) from the classmap origin, its texel = the one
// whose top-left corner it is (clipped into the raster). -> [i0, i1, fx, j0, j1, fz]
export function sideNodes(T, i, j) {
  const tz = T.PH > 1 ? (T.zs[T.PH - 1] - T.zs[0]) / (T.PH - 1) : T.texel, r = [];
  for (const [c, t, k, n] of [[T.xs, T.texel, i, T.PW], [T.zs, tz, j, T.PH]]) {
    const sp = Math.max(SIDE_M, 2.0 * t), X = c[k], Xn = Math.floor(X / sp) * sp;
    const k0 = roundEven((Xn - c[0]) / t + 0.5), k1 = k0 + roundEven(sp / t);
    r.push(clamp(k0, 0, n - 1), clamp(k1, 0, n - 1), (X - Xn) / sp);
  }
  return r;
}
// the side lattice's bilinear sign mean (surfacegen _path_side): s = the 4
// nodes' (00 10 01 11) signs (+1: the ref the lighter one, a tie too)
function sideMean(s, fx, fz) {
  return (1.0 - fz) * ((1.0 - fx) * s[0] + fx * s[1]) + fz * ((1.0 - fx) * s[2] + fx * s[3]);
}
export function haloPx(texel) {
  return Math.max(Math.max(1, roundEven(2.0 / texel)), Math.max(1, roundEven(6.0 / texel))) + 1;
}

// ---------------------------------------------------------------- noise (surfacegen _gnoise / _fbm)
const PHI = 0.6180339887498949;
const GRAD_X = new Float32Array(256), GRAD_Z = new Float32Array(256);
for (let k = 0; k < 256; k++) {
  const a = (k + 0.5) * ((2 * Math.PI) / 256);
  GRAD_X[k] = Math.cos(a); GRAD_Z[k] = Math.sin(a);
}
function grad(hx, hz, dx, dz) {
  let h = (hx + hz) >>> 0;
  h = Math.imul(h ^ (h >>> 13), 1274126177) >>> 0;
  h = (h ^ (h >>> 16)) >>> 0;
  const k = h >>> 24;
  return f32(f32(GRAD_X[k] * dx) + f32(GRAD_Z[k] * dz));
}
// gradient noise, wavelength wl, ~[0, 1]: lattice rotated 15..75 deg and
// offset per seed; lattice coordinates float64, the interpolation float32
export function gnoise(x, z, wl, seed) {
  const a = (15.0 + 60.0 * ((seed * PHI) % 1.0)) * (Math.PI / 180.0);
  const c = Math.cos(a), s = Math.sin(a);
  const fx = (x * c - z * s) * (1.0 / wl) + seed * 17.31;
  const fz = (x * s + z * c) * (1.0 / wl) + seed * 9.77;
  const ix = Math.floor(fx), iz = Math.floor(fz);
  const tx = f32(fx - ix), tz = f32(fz - iz);
  const hx0 = Math.imul(ix | 0, 374761393) >>> 0;
  const hz0 = (Math.imul(iz | 0, 668265263) + ((seed * 40503) >>> 0)) >>> 0;
  const hx1 = (hx0 + 374761393) >>> 0, hz1 = (hz0 + 668265263) >>> 0;
  const u = f32(tx * tx * tx * (tx * (tx * 6 - 15) + 10));
  const v = f32(tz * tz * tz * (tz * (tz * 6 - 15) + 10));
  const n = (grad(hx0, hz0, tx, tz) * (1 - u) + grad(hx1, hz0, tx - 1, tz) * u) * (1 - v)
    + (grad(hx0, hz1, tx, tz - 1) * (1 - u) + grad(hx1, hz1, tx - 1, tz - 1) * u) * v;
  return f32(clamp(0.5 + n, 0.0, 1.0));
}
// octave sum in [0, 1], each octave faded to its mean below the texel
function fbm(x, z, wls, seed, texel) {
  let out = 0, wsum = 0, amp = 1.0;
  for (let i = 0; i < wls.length; i++) {
    const k = octaveKeep(wls[i], texel);
    out += k > 0 ? amp * (0.5 + k * (gnoise(x, z, wls[i], seed + i * 101) - 0.5)) : amp * 0.5;
    wsum += amp;
    amp *= 0.55;
  }
  return out / wsum;
}

// ---------------------------------------------------------------- field reads
const byteAt = (st, j, i, c) => st.field[((j - st.oz) * st.ow + i - st.ox) * 8 + c];
// the four bilinear taps of a texel centre (x, z) (_bilin_idx, cell 1 m)
function taps(st, x, z) {
  const u = x - 0.5, v = z - 0.5, fx = Math.floor(u), fz = Math.floor(v);
  return {
    i0: clamp(fx, 0, st.W1 - 1), i1: clamp(fx + 1, 0, st.W1 - 1), tx: f32(clamp(u - fx, 0, 1)),
    j0: clamp(fz, 0, st.H1 - 1), j1: clamp(fz + 1, 0, st.H1 - 1), tz: f32(clamp(v - fz, 0, 1)),
  };
}
// _bilin of four float32 values
function bil4(a, b, c, d, tp) {
  const ux = f32(1 - tp.tx), uz = f32(1 - tp.tz);
  const top = f32(f32(a * ux) + f32(b * tp.tx)), bot = f32(f32(c * ux) + f32(d * tp.tx));
  return f32(f32(top * uz) + f32(bot * tp.tz));
}
const bil = (st, tp, c) => bil4(byteAt(st, tp.j0, tp.i0, c), byteAt(st, tp.j0, tp.i1, c),
  byteAt(st, tp.j1, tp.i0, c), byteAt(st, tp.j1, tp.i1, c), tp);
const decR = (v) => f32(f32(v - R_OFF) / R_SCALE);
const decG = (v) => f32(f32(v - G_OFF) / G_SCALE);
// [b0, b1): the cells whose centres lie in the texel's footprint (_block_bounds)
function block(c, texel, n) {
  return [clamp(Math.ceil(c - texel / 2.0 - 0.5), 0, n), clamp(Math.ceil(c + texel / 2.0 - 0.5), 0, n)];
}

// ---------------------------------------------------------------- set_ways
export function prepWays(field, W1, H1, look, marks, world, win = { x0: 0, z0: 0, w: W1, h: H1 }) {
  if (field.length !== win.w * win.h * 8) throw new Error(`ways field: want ${win.w} x ${win.h} x 8 bytes, got ${field.length}`);
  for (const k of ['line_m', 'kerb_line_m']) {
    const v = marks && marks[k];
    if (!(typeof v === 'number' && v > 0 && v < 1)) throw new Error(`ways field: marks.${k} ${v} is no width in (0, 1) m`);
  }
  const st = lookPart(look, world);
  Object.assign(st, { field, W1, H1, ox: win.x0, oz: win.z0, ow: win.w, oh: win.h,
    lineM: marks.line_m, kerbM: marks.kerb_line_m, ground: marks.ground || null });
  st.markCells = markCells(st);
  return st;
}
// the look's part of the ways state (the field's encoding constants, the palette's kind colours, the cross means, the
// host ring's classes): prepWays' and prepQ4's shared half
function lookPart(look, world) {
  const names = [];
  for (const [name, id] of Object.entries(look.kinds)) names[id] = name;
  const pathKind = new Array(16).fill(false);
  for (const k of PATH_KINDS) pathKind[look.kinds[k]] = true;
  // built kinds: (base, patch) per kind id, and their mean (the flat look colour)
  const lut = new Float32Array(16 * 6), M = new Float32Array(16 * 3), Many = new Array(16).fill(false);
  for (let kid = 0; kid < 16; kid++) {
    const name = names[kid];
    if (name === undefined || name === 'none') continue;
    const c = look.colour[name];
    const pe = 'palette' in c ? world.pal[c.palette] : 'base' in c ? c : null;
    if (!pe) continue;
    for (let ch = 0; ch < 3; ch++) {
      lut[kid * 6 + ch] = pe.base[ch]; lut[kid * 6 + 3 + ch] = pe.patch[ch];
      M[kid * 3 + ch] = f32(f32(lut[kid * 6 + ch] + lut[kid * 6 + 3 + ch]) / 2);
    }
    Many[kid] = lut.subarray(kid * 6, kid * 6 + 6).some((v) => v !== 0);
  }
  // share_by_sac (soil / rock), 1 for a track and every built kind
  const share = new Float32Array(256);
  for (let b = 0; b < 256; b++) {
    const k = b & KMASK, s = look.share_by_sac;
    share[b] = k === look.kinds.track || !pathKind[k] ? 1 : s[Math.min(b >> KIND_BITS, s.length - 1)];
  }
  const st = { look, names, pathKind, lut, M, Many, share, kSteps: look.kinds.steps, crossMean: new Map() };
  // the cross-section's mean across the width (linspace(0, 1, 2001))
  for (const k of PATH_KINDS) {
    for (let hi = 0; hi < 16; hi++) {
      const b = look.kinds[k] | (hi << KIND_BITS), sum = {};
      for (let q = 0; q <= 2000; q++) {
        const cf = cross(st, b, f32(q / 2000));
        for (const p in cf) sum[p] = (sum[p] || 0) + cf[p];
      }
      for (const p in sum) sum[p] /= 2001;
      st.crossMean.set(b, sum);
    }
  }
  const hc = look.colour.host, ids = { ...world.ids, none: world.noneId };
  st.bare = new Set(hc.bare.map((k) => ids[k]));
  st.bareHi = new Set(hc.bare_above_treeline.map((k) => ids[k]));
  if (st.bareHi.size) { st.treeline = world.treelineM; st.treeSoft = hc.treeline_soft_m; }
  st.luma = look.steps.luma || null;
  st.L0 = lum(M[st.kSteps * 3], M[st.kSteps * 3 + 1], M[st.kSteps * 3 + 2]);
  st.markCells = new Map();
  return st;
}

// per 1 m mark cell (P1 code 5-7) of st.field the coarse marks' (F, C) — the
// mean of S x S sub-samples (prepWays; groundgen.js: a slot's field window)
export function markCells(st) {
  const out = new Map(), { W1 } = st;
  for (let j = st.oz; j < st.oz + st.oh; j++) {
    for (let i = st.ox; i < st.ox + st.ow; i++) {
      if ((byteAt(st, j, i, 6) & CMASK) < MARK0) continue;
      if (!st.ground) throw new Error('ways field holds ground marks (codes 5-7) but world.json ways_field.marks has no ground — rerun the WORLD stage');
      let F = 0, C0 = 0, C1 = 0, C2 = 0;
      for (let b = 0; b < MARK_S; b++) {
        for (let a = 0; a < MARK_S; a++) {
          const fc = markFc(st, i + (a + 0.5) / MARK_S, j + (b + 0.5) / MARK_S, 1.0 / MARK_S);
          if (fc) { F += fc[0]; C0 += fc[1]; C1 += fc[2]; C2 += fc[3]; }
        }
      }
      const n = MARK_S * MARK_S;
      out.set(j * W1 + i, [f32(F / n), f32(C0 / n), f32(C1 / n), f32(C2 / n)]);
    }
  }
  return out;
}

// ---------------------------------------------------------------- per tile
export function waysTile(st, x0, z0, w_m, h_m, pw, ph) {
  const texel = w_m / pw, tzm = h_m / ph, m = haloPx(texel);
  const PW = pw + 2 * m, PH = ph + 2 * m;
  const xs = new Float32Array(PW), zs = new Float32Array(PH);
  for (let k = 0; k < PW; k++) xs[k] = f32(f32(x0) + f32((k - m + 0.5) * f32(texel)));
  for (let k = 0; k < PH; k++) zs[k] = f32(f32(z0) + f32((k - m + 0.5) * f32(tzm)));
  const fine = texel < 1.0;
  // the window _path_pass reads (_path_span)
  let c0, c1, r0, r1;
  if (st.q4) {
    // WAYS-Q4: the 4 m cells under the raster; nothing within half a cell of a strip: null
    if (texel < Q4.M) throw new Error(`waysTile: a ${texel} m texel reads the 1 m field, not the 4 m mass`);
    [c0] = qBlock(xs[0], texel, st.QW); c1 = qBlock(xs[PW - 1], texel, st.QW)[1];
    [r0] = qBlock(zs[0], texel, st.QH); r1 = qBlock(zs[PH - 1], texel, st.QH)[1];
    if (c1 <= c0 || r1 <= r0) return null;
    let rq = 255;
    for (let j = r0; j < r1; j++) for (let i = c0; i < c1; i++) rq = Math.min(rq, qAt(st, j, i, 8));
    if (rq >= R_OFF + R_SCALE / 2) return null;
  } else if (fine) {
    c0 = Math.max(0, taps(st, xs[0], 0.5).i0 - KIND_PAD); c1 = Math.min(st.W1, taps(st, xs[PW - 1], 0.5).i1 + 1 + KIND_PAD);
    r0 = Math.max(0, taps(st, 0.5, zs[0]).j0 - KIND_PAD); r1 = Math.min(st.H1, taps(st, 0.5, zs[PH - 1]).j1 + 1 + KIND_PAD);
  } else {
    [c0] = block(xs[0], texel, st.W1); c1 = block(xs[PW - 1], texel, st.W1)[1];
    [r0] = block(zs[0], texel, st.H1); r1 = block(zs[PH - 1], texel, st.H1)[1];
  }
  if (c1 <= c0 || r1 <= r0) return null;
  // nothing within half a cell of a strip: no texel gains way colour
  let rmin = 255, treads = false;
  if (st.q4) rmin = 0;
  else for (let j = r0; j < r1; j++) {
    for (let i = c0; i < c1; i++) { rmin = Math.min(rmin, byteAt(st, j, i, 0)); if ((byteAt(st, j, i, 6) & CMASK) === CODE.treads) treads = true; }
  }
  if (rmin >= R_OFF + R_SCALE / 2) return null;
  const look = st.look, nz = look.noise, ew = look.edge_ways;
  const T = {
    texel, tex: f32(texel), m, pw, ph, PW, PH, xs, zs, fine,
    kc: octaveKeep(CROSS_WL, texel), pk: octaveKeep(nz.patch_wl_m, texel),
    amp: nz.edge_amp_m * octaveKeep(nz.edge_wl_m, texel),
    ea: -Math.max(look.edge.in_m, texel / 2.0), eb: Math.max(look.edge.soft_m, texel / 2.0),
    wa: -Math.max(ew.in_m, texel / 2.0), wb: Math.max(ew.soft_m, texel / 2.0),
    hh: Math.max(0.03, texel / 2.0), area: f32(texel * texel), treadKa: new Map(),
  };
  // the worn edge band's end (fine regime; inside WORN_REACH, the wobble included)
  T.wn = Math.max(T.eb, Math.min(T.eb + look.colour.worn.width_m, WORN_REACH - Math.max(T.amp, 0.0)));
  if (fine && treads) {
    // the stair bytes' tread ka (no tread cell in the span: none — the loop would find none): one per byte per tile, from the MEDIAN pitch
    // over every texel the byte paints (np.median in _way_colour_fine)
    const P = new Map(), tr = new Set(), period = f32(look.steps.phase_period_m);
    for (let j = 0; j < PH; j++) {
      for (let i = 0; i < PW; i++) {
        const tp = taps(st, xs[i], zs[j]);
        const wb = 1 - smooth(decR(bil(st, tp, 0)), T.wa, T.wb);
        if (!(wb > 0)) continue;
        const b6 = byteAt(st, tp.j0, tp.i0, 6);
        const pitch = f32(period / Math.max(2 * (b6 >> CODE_BITS), 1));
        const treads = sameTaps(st, tp, 6) && (b6 & CMASK) === CODE.treads;
        for (const [bv, kb] of texelKinds(st, tp)) {
          if (st.pathKind[bv & KMASK] || !(kb === null || kb > 0)) continue;
          if (!P.has(bv)) P.set(bv, []);
          P.get(bv).push(pitch);
          if (treads) tr.add(bv);
        }
      }
    }
    for (const bv of tr) {
      const a = Float32Array.from(P.get(bv)).sort(), n = a.length;
      const med = n & 1 ? a[n >> 1] : f32(f32(a[n / 2 - 1] + a[n / 2]) / 2);
      T.treadKa.set(bv, octaveKeep(med, texel));
    }
  }
  return T;
}

// ---------------------------------------------------------------- passes 1 + 2: the host ring
// _path_host / _steps_luma: hb (the bare-host share), Hs (the host albedo)
// and fL (the stair luminance factor), box means over the texels at least
// ring_m off every strip. -> Float32Array PH x PW x 5
export function hostPass(st, T, rgb, cmAt, elevAt) {
  const { PW, PH, texel } = T, look = st.look, hc = look.colour.host, lm = st.luma;
  const N = PW * PH, C = 12;
  const P = new Float32Array(N * C);
  // pass 1: the ring masks x what they average
  for (let j = 0; j < PH; j++) {
    for (let i = 0; i < PW; i++) {
      const q = j * PW + i, o = q * C;
      // the ring: the texel centre's dEdge past ring_m + half a texel; WAYS-Q4: the texel's min dEdge past ring_m
      const dt = st.q4 ? f32(q4Dmin(st, T, i, j) + texel / 2.0) : decR(bil(st, taps(st, T.xs[i], T.zs[j]), 0));
      const cls = cmAt(i, j);
      let bare = st.bare.has(cls) ? 1 : 0;
      if (st.bareHi.has(cls)) bare = Math.max(bare, f32(smooth(elevAt(i, j), st.treeline - st.treeSoft, st.treeline + st.treeSoft)));
      const ring = dt >= hc.ring_m + texel / 2.0 ? 1 : 0;
      const r = rgb[q * 3], g = rgb[q * 3 + 1], b = rgb[q * 3 + 2];
      P[o] = ring; P[o + 1] = bare * ring; P[o + 2] = bare;
      P[o + 3] = r * ring; P[o + 4] = g * ring; P[o + 5] = b * ring;
      P[o + 6] = r; P[o + 7] = g; P[o + 8] = b;
      if (lm) {
        const rl = dt >= lm.ring_m + texel / 2.0 ? 1 : 0, L = f32(lum(r, g, b));
        P[o + 9] = rl; P[o + 10] = L * rl; P[o + 11] = L;
      }
    }
  }
  // pass 2: the box means (clipped to the raster, _boxblur), separable
  const rh = Math.max(1, roundEven(hc.radius_m / texel)), rl = lm ? Math.max(1, roundEven(lm.radius_m / texel)) : 0;
  const rad = (c) => (c < 9 ? rh : rl), nc = lm ? 12 : 9;
  const S = new Float64Array(N * C);
  for (let j = 0; j < PH; j++) {
    for (let i = 0; i < PW; i++) {
      for (let c = 0; c < nc; c++) {
        const r = rad(c);
        let s = 0;
        for (let k = Math.max(0, i - r); k <= Math.min(PW - 1, i + r); k++) s += P[(j * PW + k) * C + c];
        S[(j * PW + i) * C + c] = s;
      }
    }
  }
  const out = new Float32Array(N * 5);
  const B = new Float64Array(C);
  for (let j = 0; j < PH; j++) {
    for (let i = 0; i < PW; i++) {
      for (let c = 0; c < nc; c++) {
        const r = rad(c), y0 = Math.max(0, j - r), y1 = Math.min(PH - 1, j + r);
        let s = 0;
        for (let k = y0; k <= y1; k++) s += S[(k * PW + i) * C + c];
        B[c] = f32(s / ((y1 - y0 + 1) * (Math.min(PW - 1, i + r) - Math.max(0, i - r) + 1)));
      }
      const o = (j * PW + i) * 5, ok = B[0] > 1e-3;
      out[o] = clamp(ok ? f32(B[1] / B[0]) : B[2], 0, 1);
      for (let ch = 0; ch < 3; ch++) out[o + 1 + ch] = ok ? f32(B[3 + ch] / B[0]) : B[6 + ch];
      if (lm) {
        const Lg = B[9] > 1e-3 ? f32(B[10] / B[9]) : B[11];
        const Lt = clamp(Math.min(Math.max(f32(st.L0), lm.ring_k[0] * Lg), lm.ring_k[1] * Lg), lm.floor, lm.ceil);
        out[o + 4] = f32(Lt / f32(Math.max(st.L0, 1e-3)));
      } else out[o + 4] = 1;
    }
  }
  return out;
}

// ---------------------------------------------------------------- PATH-G: the trodden look
// cross-section coefficients of look byte b at |t| = u (_path_cross); hw
// (fine regime: the way's half width) widens the soil / rock tread's half-weight
// point to cross.tread_min_m[sac] / 2, never past the drawn edge
function cross(st, b, u, hw = null) {
  const look = st.look, kind = b & KMASK, sac = b >> KIND_BITS;
  if (kind === look.kinds.track) {
    const tr = look.track, rs = look.colour.track.rut_soft_t, [r0, r1] = tr.rut_t, hg = tr.centre_host_by_grade;
    const hc = hg[Math.min(sac, hg.length - 1)] * (1.0 - smooth(u, tr.centre_t - rs, tr.centre_t + rs));
    const sh = smooth(u, r1, r1 + 2.0 * rs);
    const rw = smooth(u, r0 - rs, r0 + rs) * (1.0 - smooth(u, r1 - rs, r1 + rs));
    const rest = 1.0 - hc;
    return { tread: rest * (1.0 - sh) * (1.0 - rw), rut: rest * (1.0 - sh) * rw, shoulder: rest * sh, host: hc };
  }
  const cr = look.colour.cross, tmin = cr.tread_min_m[Math.min(sac, cr.tread_min_m.length - 1)];
  let tt = cr.tread_t;
  if (hw !== null && tmin > 0) tt = Math.max(tt + 0.5 * cr.soft_t, Math.min(f32(0.5 * tmin) / hw, 1.0)) - 0.5 * cr.soft_t;
  const tw = 1.0 - smooth(u, tt, tt + cr.soft_t);
  return { tread: tw, shoulder: 1.0 - tw };
}
// out += w (path colour - host) for a PATH-G look byte at one texel; H = the
// host colour, hx / q = the host ring (hb, Hs) at the texel; nb = the worn
// edge band's weight (fine regime) or 0
function addPath(st, T, out, H, x, z, bv, w, cf, hx, q, nb = 0) {
  const look = st.look, nz = look.noise, c = look.colour[st.names[bv & KMASK]], hc = look.colour.host;
  const p = 0.5 + T.pk * (gnoise(x, z, nz.patch_wl_m, nz.seed + 1) - 0.5);
  const m = (fbm(x, z, [9.5, 4.3], 23, T.texel) - 0.5) * (2.0 * nz.mid);
  const ha = c.host_aware || 0, k = hx[q] * ha;
  const L = lum(hx[q + 1], hx[q + 2], hx[q + 3]);
  let tread = [0, 0, 0], shoulder = [0, 0, 0], rut = [0, 0, 0];
  const tm = [0, 0, 0];
  let tmf = tm;
  for (let ch = 0; ch < 3; ch++) {
    tread[ch] = c.tread[ch] * (1 - p) + c.patch[ch] * p; shoulder[ch] = c.shoulder[ch]; rut[ch] = c.rut ? c.rut[ch] : 0;
    tm[ch] = (c.tread[ch] + c.patch[ch]) * 0.5;
    if (ha > 0) {
      // over a bare host: the host's own albedo, lighter, warmer, less saturated
      const hd = L + hc.sat * (hx[q + 1 + ch] - L), g = hc.gain, wm = hc.warm[ch];
      tread[ch] = tread[ch] * (1 - k) + (hd * (g.tread * (1 - p) + g.patch * p) + wm) * k;
      tm[ch] = tm[ch] * (1 - k) + (hd * (0.5 * (g.tread + g.patch)) + wm) * k;
      shoulder[ch] = shoulder[ch] * (1 - k) + (hd * g.shoulder + wm) * k;
      rut[ch] = rut[ch] * (1 - k) + (hd * g.rut + wm) * k;
    }
  }
  // the contrast floor against the host ring (colour.contrast)
  const dl = look.colour.contrast.dl_min;
  if (dl > 0) {
    const Lh = lstarY(hx[q + 1], hx[q + 2], hx[q + 3])[2];
    // the side lattice: the noise-free tread / shoulder / rut over each node's host ring (the same mix)
    const t = q / 5, [i0, i1, fx, j0, j1, fz] = sideNodes(T, t % T.PW, Math.floor(t / T.PW));
    const g = hc.gain, c0s = [[0, 1, 2].map((ch) => (c.tread[ch] + c.patch[ch]) * 0.5), c.shoulder, c.rut || [0, 0, 0]];
    const gs = [0.5 * (g.tread + g.patch), g.shoulder, g.rut];
    const sg = [[], [], []];
    for (const [jn, inn] of [[j0, i0], [j0, i1], [j1, i0], [j1, i1]]) {
      const qn = (jn * T.PW + inn) * 5, kn = hx[qn] * ha, Ln = lum(hx[qn + 1], hx[qn + 2], hx[qn + 3]);
      const Lhn = lstarY(hx[qn + 1], hx[qn + 2], hx[qn + 3])[2];
      for (let p = 0; p < 3; p++) {
        const r = [0, 0, 0];
        for (let ch = 0; ch < 3; ch++) {
          r[ch] = c0s[p][ch];
          if (ha > 0) r[ch] = r[ch] * (1 - kn) + ((Ln + hc.sat * (hx[qn + 1 + ch] - Ln)) * gs[p] + hc.warm[ch]) * kn;
        }
        sg[p].push(lstarY(r[0], r[1], r[2])[2] >= Lhn ? 1 : -1);
      }
    }
    tread = pathFloor(tread, tm, Lh, dl, sideMean(sg[0], fx, fz));
    tmf = pathFloor(tm, tm, Lh, dl, sideMean(sg[0], fx, fz));
    shoulder = pathFloor(shoulder, shoulder, Lh, dl, sideMean(sg[1], fx, fz));
    if (c.rut) rut = pathFloor(rut, rut, Lh, dl, sideMean(sg[2], fx, fz));
  }
  shoulder = shoulder.map((v, ch) => f32(f32(v * f32(1 - c.host_keep)) + f32(H[ch] * f32(c.host_keep))));
  // the earth floor (colour.contrast, paths-earth): over a green host the
  // tread, the shoulder (after its host_keep mix) and the rut keep an earth
  // hue, c_min and de_min from the ring Hs and the host H
  const ec = look.colour.contrast, earth = (ec.de_min > 0 || ec.c_min > 0) && ec.earth_kinds.includes(st.names[bv & KMASK]);
  let vg = 0;
  if (earth || nb > 0) {
    const hsLab = labOf([hx[q + 1], hx[q + 2], hx[q + 3]]);
    vg = vegWeight(hsLab, ec.veg_a);
    if (earth) {
      const hosts = [hsLab, labOf(H)];
      tread = earthFloor(tread, tmf, hosts, vg, ec);
      shoulder = earthFloor(shoulder, shoulder, hosts, vg, ec);
      if (c.rut) rut = earthFloor(rut, rut, hosts, vg, ec);
    }
  }
  for (let ch = 0; ch < 3; ch++) {
    const col = { tread: tread[ch] + m, shoulder: shoulder[ch], host: H[ch], rut: rut[ch] + m };
    let pc = 0;
    for (const part in cf) pc += cf[part] * col[part];
    out[ch] += w * (pc - H[ch]);
  }
  // the worn edge band: trampled host grass, lighter and yellower, over a green host only
  if (nb > 0) {
    const wn = look.colour.worn, k = f32(nb * f32(vg));
    for (let ch = 0; ch < 3; ch++) out[ch] += k * f32(f32(H[ch] * f32(wn.gain - 1.0)) + wn.warm[ch]);
  }
}

// ---------------------------------------------------------------- the kind blend (_path_kinds)
const sameTaps = (st, tp, c) => {
  const b = byteAt(st, tp.j0, tp.i0, c);
  return b === byteAt(st, tp.j0, tp.i1, c) && b === byteAt(st, tp.j1, tp.i0, c) && b === byteAt(st, tp.j1, tp.i1, c);
};
// fine regime: [[look byte, blend weight | null = 1]] at a texel. One byte
// (and no kind-none cell) under the [1 2 1] tent of the four taps: it alone;
// several: each byte's cell mask through the tent, bilinear, normalised (byte
// 0 of a kind-none way, R < 255, normalises, never paints). The tent reads
// the 4 x 4 cells round the taps (edge-replicated: clamped at the field)
function texelKinds(st, tp) {
  const cj = [tp.j0, tp.j0, tp.j1, tp.j1], ci = [tp.i0, tp.i1, tp.i0, tp.i1];
  const bytes = [];
  let none = false;
  for (let t = 0; t < 4; t++) {
    for (let dj = -1; dj <= 1; dj++) {
      for (let di = -1; di <= 1; di++) {
        const j = clamp(cj[t] + dj, 0, st.H1 - 1), i = clamp(ci[t] + di, 0, st.W1 - 1), b = byteAt(st, j, i, 2);
        if (b) { if (!bytes.includes(b)) bytes.push(b); } else if (byteAt(st, j, i, 0) < 255) none = true;
      }
    }
  }
  if (bytes.length <= 1 && !none) return bytes.map((b) => [b, null]);
  bytes.sort((a, b) => a - b);
  const W = [0.25, 0.5, 0.25];
  const tent = (t, v) => {
    let s = 0;
    for (let dj = -1; dj <= 1; dj++) {
      for (let di = -1; di <= 1; di++) {
        const j = clamp(cj[t] + dj, 0, st.H1 - 1), i = clamp(ci[t] + di, 0, st.W1 - 1);
        const b = byteAt(st, j, i, 2);
        if (v === 0 ? b === 0 && byteAt(st, j, i, 0) < 255 : b === v) s += W[dj + 1] * W[di + 1];
      }
    }
    return s;
  };
  const g = bytes.map((v) => bil4(tent(0, v), tent(1, v), tent(2, v), tent(3, v), tp));
  let s = 0;
  for (const v of g) s = f32(s + v);
  if (none) s = f32(s + bil4(tent(0, 0), tent(1, 0), tent(2, 0), tent(3, 0), tp));
  const inv = s > 0 ? f32(1.0 / s) : 0;
  return bytes.map((b, k) => [b, f32(g[k] * inv)]);
}

// ---------------------------------------------------------------- built kinds, fine regime
// the steps' (nosing, riser) albedo for a tread tr (_steps_edge_colours)
function stepsEdge(st, tr) {
  const s = st.look.steps, lm = st.luma;
  if (!lm) return [s.colour.nosing, s.colour.riser];
  return [tr.map((v) => v * f32(lm.nosing_k)), tr.map((v) => v * f32(lm.riser_k))];
}
// the built features at a texel (_way_features): P1 only where the four taps
// share P1.B, P0.A only where all four carry a side strip
function features(st, T, i, j, tp, ts) {
  const F = {};
  F.sideOk = byteAt(st, tp.j0, tp.i0, 3) > 0 && byteAt(st, tp.j0, tp.i1, 3) > 0
    && byteAt(st, tp.j1, tp.i0, 3) > 0 && byteAt(st, tp.j1, tp.i1, 3) > 0 ? 1 : 0;
  F.dS = f32(f32(bil(st, tp, 3) - A_OFF) / A_SCALE);
  const b0 = byteAt(st, tp.j0, tp.i0, 6);
  F.code = sameTaps(st, tp, 6) ? b0 & CMASK : 0;
  F.K = 2.0 * (b0 >> CODE_BITS);
  const v4 = bil(st, tp, 4), v5 = bil(st, tp, 5), v7 = bil(st, tp, 7);
  F.dL = f32(f32(v4 - L_OFF) / L_SCALE);
  F.dD = f32(f32(v5 - D_OFF) / D_SCALE);
  F.fm = f32(f32(v4 - D_OFF) / D_SCALE);          // treads: the flight mask
  F.phi = f32(npMod(f32(f32(f32(Math.atan2(f32(v7 - P_OFF), f32(v5 - P_OFF))) / (2.0 * Math.PI)) * F.K), Math.max(F.K, 1.0)));
  F.hw = hwAt(st, T, i, j, ts);
  F.t = ts;
  return F;
}
// the way's half width at raster texel (i, j) from the cross gradient over the
// raster (np.gradient: one-sided at its edges); ts = the texel's own signed t
function hwAt(st, T, i, j, ts) {
  const tAt = (ii, jj) => decG(bil(st, taps(st, T.xs[ii], T.zs[jj]), 1));
  const d1 = (n, k, at) => (k === 0 ? f32(at(1) - at(0)) / T.texel : k === n - 1 ? f32(at(n - 1) - at(n - 2)) / T.texel
    : f32(at(k + 1) - at(k - 1)) / (2.0 * T.texel));
  const gx = f32(d1(T.PW, i, (k) => (k === i ? ts : tAt(k, j))));
  const gz = f32(d1(T.PH, j, (k) => (k === j ? ts : tAt(i, k))));
  return 1.0 / Math.max(Math.hypot(gx, gz), 0.02);
}
// a built kind's albedo at a texel (_way_colour_fine): the flat look colour,
// the side strip + kerb line, lane lines, sleepers, treads — each the texel's
// box coverage, periodic ones faded to their mean below 2 texels per period
function colourFine(st, T, bv, F) {
  const look = st.look, M = st.M, tex = T.tex, texel = T.texel, hh = T.hh;
  const k = bv & KMASK, h = bv >> KIND_BITS;
  const tr = [M[k * 3], M[k * 3 + 1], M[k * 3 + 2]];
  let col = tr.slice();
  const mix = (w, c) => { col = col.map((v, ch) => v + w * (c[ch] - v)); };
  if (h && st.Many[h]) {
    mix(smooth(F.dS, -hh, hh) * F.sideOk, [M[h * 3], M[h * 3 + 1], M[h * 3 + 2]]);
    mix(boxCov(F.dS, 0.0, st.kerbM, tex) * F.sideOk, look.kerb.colour);
  }
  if (F.code === CODE.centre || F.code === CODE.lane) {
    mix(boxCov(F.dL, -st.lineM / 2, st.lineM / 2, tex) * clamp(0.5 + F.dD / tex, 0.0, 1.0), look.lines.colour);
  }
  if (F.code === CODE.sleepers) {
    const bl = look.ballast, pitch = bl.sleeper_pitch_m, sw = bl.sleeper_w_m, sl = bl.sleeper_len_m;
    const x = (F.phi - roundEven(F.phi)) * pitch;
    const ka = octaveKeep(pitch, texel);
    const ca = ka * boxCov(x, -sw / 2, sw / 2, tex) + (1 - ka) * (sw / pitch);
    mix(ca * boxCov(F.t * F.hw, -sl / 2, sl / 2, tex), bl.colour_sleeper);
  }
  if (F.code === CODE.treads) {
    const s = look.steps, pitch = f32(f32(s.phase_period_m) / Math.max(F.K, 1.0));
    const x = (F.phi - Math.floor(F.phi)) * pitch;             // 0 at riser k, uphill to the next
    const nos = s.nosing_m, rb = 0.5 * s.riser_m, ka = T.treadKa.get(bv);
    const wn = ka * (boxCov(x, 0.0, nos, tex) + boxCov(x - pitch, 0.0, nos, tex)) + (1 - ka) * (nos / pitch);
    const wr = ka * (boxCov(x, pitch - rb, pitch, tex) + boxCov(x + pitch, pitch - rb, pitch, tex)) + (1 - ka) * (rb / pitch);
    const wf = smooth(F.fm, -hh, hh);
    const [cn, cr] = stepsEdge(st, tr);
    mix(wf * wn, cn);
    mix(wf * wr, cr);
  }
  return col;
}

// ---------------------------------------------------------------- coarse regime (texel >= 1 m)
// the PATH-G bytes' area coverage under texel (i, j): {byte: sum of the
// cells' (0.5 - dEdge) x share / texel area}
function coarseAcc(st, T, i, j) {
  const [bx0, bx1] = block(T.xs[i], T.texel, st.W1), [bz0, bz1] = block(T.zs[j], T.texel, st.H1);
  const acc = new Map();
  for (let cz = bz0; cz < bz1; cz++) {
    for (let cx = bx0; cx < bx1; cx++) {
      const b = byteAt(st, cz, cx, 2);
      if (!st.pathKind[b & KMASK]) continue;
      const cov = f32(clamp(0.5 - decR(byteAt(st, cz, cx, 0)), 0.0, 1.0));
      if (cov > 0) acc.set(b, (acc.get(b) || 0) + f32(cov * st.share[b]));
    }
  }
  for (const [b, s] of acc) acc.set(b, f32(s / T.area));
  return acc;
}
// a kind change ramps over one texel's [1 2 1] tent (texel <= 2 m), the
// texel's total kept; one byte in the 3 x 3 texels: the coverage as is
function coarseBlend(st, T, i, j) {
  const acc = coarseAcc(st, T, i, j);
  if (T.texel > KIND_BLEND_TEXEL) return acc;
  const nb = [];
  const bytes = new Set(acc.keys());
  for (let dj = -1; dj <= 1; dj++) {
    const row = [];
    for (let di = -1; di <= 1; di++) {
      const a = di === 0 && dj === 0 ? acc : coarseAcc(st, T, clamp(i + di, 0, T.PW - 1), clamp(j + dj, 0, T.PH - 1));
      for (const b of a.keys()) bytes.add(b);
      row.push(a);
    }
    nb.push(row);
  }
  if (bytes.size <= 1) return acc;
  const tent3 = (b) => {
    const r = [0, 1, 2].map((c) => f32(f32(f32((nb[0][c].get(b) || 0) + f32(2.0 * (nb[1][c].get(b) || 0))) + (nb[2][c].get(b) || 0)) * 0.25));
    return f32(f32(f32(r[0] + f32(2.0 * r[1])) + r[2]) * 0.25);
  };
  const sorted = [...bytes].sort((a, b) => a - b), tb = sorted.map(tent3);
  let tot = 0, s = 0;
  for (const b of sorted) tot = f32(tot + (acc.get(b) || 0));
  for (const v of tb) s = f32(s + v);
  const f = s > 0 ? f32(tot / s) : 0;
  return new Map(sorted.map((b, k) => [b, f32(tb[k] * f)]));
}
// ---------------------------------------------------------------- WAYS-Q4: the ways field's 4 m mass (every level)
// The levels whose texel is >= Q4.M (j > WAYS_MAX_J: L4, L5 — the VT's pinned fallback) read no 1 m field (a slot's
// window there is 2–4 k cells a side) but its 4 m MASS, LOOK-FREE data (APP_GENSURF §5e "WAYS-Q4"): per 4 m cell
// (x in [4 i, 4 i + 4)) up to Q4.N entries (an id: a PATH-G look byte, or a built kind id — a built cell's side strip
// counted to its side kind) with their coverage (the mean over the 16 cells of the coarse regime's cov =
// clamp(0.5 - dEdge), u8 / 255), + the cells' min dEdge byte. More entries: the rest folds into the largest (its mass
// kept). Cell bytes (Q4.B): [e0 e1 e2 e3, c0 c1 c2 c3, rmin 0 0 0] — the served tile surface/ways/q4/<tx>_<ty>.png is
// those three RGBA planes stacked (Q4.TILE x 3 Q4.TILE). ONE function makes it: engine/steps/paint_q4 and the gates.
export const Q4 = { M: 4, N: 4, B: 12, TILE: 128, MAXP: 4, K: f32(16 / 255) };
// field: w x h x 8 (window-local cells, w / h multiples of 4), kinds: the field's {name: id} (world.json ways_field.kinds)
// -> {cells: Uint8Array (w / 4) (h / 4) Q4.B, qw, qh, folded (cells past Q4.N entries), entries (cells with any)}
export function q4Cells(field, w, h, kinds) {
  if (w % Q4.M || h % Q4.M || field.length !== w * h * 8) throw new Error(`q4Cells: a ${w} x ${h} field (${field.length} bytes) is no whole 4 m grid of 8-byte cells`);
  const pk = new Array(16).fill(false);
  for (const k of PATH_KINDS) { if (!Number.isInteger(kinds[k])) throw new Error(`q4Cells: the field's kinds lack ${k}`); pk[kinds[k]] = true; }
  const qw = w / Q4.M, qh = h / Q4.M, cells = new Uint8Array(qw * qh * Q4.B), acc = new Map();
  let folded = 0, entries = 0;
  for (let qz = 0; qz < qh; qz++) {
    for (let qx = 0; qx < qw; qx++) {
      acc.clear();
      let rmin = 255;
      for (let z = qz * Q4.M; z < qz * Q4.M + Q4.M; z++) {
        for (let x = qx * Q4.M; x < qx * Q4.M + Q4.M; x++) {
          const o = 8 * (z * w + x), R = field[o], B = field[o + 2], A = field[o + 3];
          if (R < rmin) rmin = R;
          const k = B & KMASK, cov = f32(clamp(0.5 - decR(R), 0.0, 1.0));
          if (k === 0 || !(cov > 0)) continue;
          if (pk[k]) { acc.set(B, (acc.get(B) || 0) + cov); continue; }
          const hs = B >> KIND_BITS, s = A > 0 && hs > 0 ? f32(clamp(0.5 + f32(f32(A - A_OFF) / A_SCALE), 0.0, 1.0)) : 0;
          if (s < 1) acc.set(k, (acc.get(k) || 0) + cov * (1 - s));
          if (s > 0) acc.set(hs, (acc.get(hs) || 0) + cov * s);
        }
      }
      const o = Q4.B * (qz * qw + qx);
      cells[o + 8] = rmin;
      const E = [...acc].map(([id, v]) => [id, v]).filter(([, v]) => v > 0).sort((a, b) => b[1] - a[1] || a[0] - b[0]);
      if (!E.length) continue;
      entries++;
      if (E.length > Q4.N) { folded++; for (const [, v] of E.splice(Q4.N)) E[0][1] += v; }
      for (let e = 0; e < E.length; e++) {
        cells[o + e] = E[e][0];
        cells[o + 4 + e] = Math.min(255, Math.round((E[e][1] / (Q4.M * Q4.M)) * 255));
      }
    }
  }
  return { cells, qw, qh, folded, entries };
}
// the served tile's planes (n x 3 n RGBA, plane p = rows [p n, p n + n)) <-> the cells (n x n x Q4.B)
export function q4ToPlanes(cells, n) {
  const out = new Uint8Array(n * 3 * n * 4);
  for (let q = 0; q < n * n; q++) for (let p = 0; p < 3; p++) out.set(cells.subarray(Q4.B * q + 4 * p, Q4.B * q + 4 * p + 4), 4 * (p * n * n + q));
  return out;
}
export function q4FromPlanes(rgba, n) {
  if (rgba.length !== n * 3 * n * 4) throw new Error(`q4FromPlanes: ${rgba.length} bytes is no ${n} x ${3 * n} RGBA tile`);
  const out = new Uint8Array(n * n * Q4.B);
  for (let q = 0; q < n * n; q++) for (let p = 0; p < 3; p++) out.set(rgba.subarray(4 * (p * n * n + q), 4 * (p * n * n + q) + 4), Q4.B * q + 4 * p);
  return out;
}
// the q4 state (prepWays' twin for the levels past WAYS_MAX_J): cells = a window [x0, x0 + w) x [z0, z0 + h) of the
// QW x QH world grid of 4 m cells (Q4.B bytes each); look / world as prepWays
export function prepQ4(cells, QW, QH, look, world, win = { x0: 0, z0: 0, w: QW, h: QH }) {
  if (cells.length !== win.w * win.h * Q4.B) throw new Error(`q4 cells: want ${win.w} x ${win.h} x ${Q4.B} bytes, got ${cells.length}`);
  return Object.assign(lookPart(look, world), { q4: true, cells, QW, QH, ox: win.x0, oz: win.z0, ow: win.w, oh: win.h });
}
const qAt = (st, j, i, c) => st.cells[((j - st.oz) * st.ow + i - st.ox) * Q4.B + c];
// [b0, b1): the 4 m cells whose centres (4 q + 2) lie in the texel's footprint (block's twin)
function qBlock(c, texel, n) {
  return [clamp(Math.ceil((c - texel / 2.0 - Q4.M / 2) / Q4.M), 0, n), clamp(Math.ceil((c + texel / 2.0 - Q4.M / 2) / Q4.M), 0, n)];
}
// the min dEdge (m) over the texel's 4 m cells (the host ring's test at Q4: the whole texel at least ring_m off)
function q4Dmin(st, T, i, j) {
  const [bx0, bx1] = qBlock(T.xs[i], T.texel, st.QW), [bz0, bz1] = qBlock(T.zs[j], T.texel, st.QH);
  let r = 255;
  for (let cz = bz0; cz < bz1; cz++) for (let cx = bx0; cx < bx1; cx++) r = Math.min(r, qAt(st, cz, cx, 8));
  return decR(r);
}
// the Q4 texel: the PATH-G bytes at their cross mean (the first Q4.MAXP met, as the GPU's arrays), the built kinds at
// their flat look colour M (SEALED-MAT's; the steps x fL), every entry its area coverage; no sub-texel feature
function q4Texel(st, T, i, j, rgb, hx, q, fL) {
  const x = T.xs[i], z = T.zs[j], out = [rgb[0], rgb[1], rgb[2]];
  const [bx0, bx1] = qBlock(x, T.texel, st.QW), [bz0, bz1] = qBlock(z, T.texel, st.QH);
  const pb = [], pv = [], ac = [0, 0, 0], as = [0, 0, 0];
  let aw = 0;
  for (let cz = bz0; cz < bz1; cz++) {
    for (let cx = bx0; cx < bx1; cx++) {
      for (let e = 0; e < Q4.N; e++) {
        const id = qAt(st, cz, cx, e);
        if (!id) continue;
        const c = f32(qAt(st, cz, cx, 4 + e) * Q4.K);
        if (st.pathKind[id & KMASK]) {
          let s = pb.indexOf(id);
          if (s < 0 && pb.length < Q4.MAXP) { s = pb.length; pb.push(id); pv.push(0); }
          if (s >= 0) pv[s] = f32(pv[s] + f32(c * st.share[id]));
          continue;
        }
        if (!st.Many[id]) continue;
        const a = id === st.kSteps ? as : ac;
        aw = f32(aw + c);
        for (let ch = 0; ch < 3; ch++) a[ch] = f32(a[ch] + f32(c * st.M[id * 3 + ch]));
      }
    }
  }
  for (let s = 0; s < pb.length; s++) {
    const w = f32(pv[s] / T.area);
    if (w > 0) addPath(st, T, out, rgb, x, z, pb[s], w, st.crossMean.get(pb[s]), hx, q);
  }
  const fw = f32(aw / T.area);
  for (let ch = 0; ch < 3; ch++) out[ch] += f32(ac[ch] / T.area) - fw * rgb[ch] + f32(as[ch] / T.area) * fL;
  return out;
}

// per 1 m cell the built kind's mean albedo, every feature as its cell area
// fraction (_way_cells; the sleepers' |grad t| a central difference over the
// field, one-sided at its edges)
function wayCell(st, j, i) {
  const look = st.look, M = st.M;
  const B = byteAt(st, j, i, 2), k = B & KMASK, h = st.pathKind[k] ? 0 : B >> KIND_BITS;
  let col = [M[k * 3], M[k * 3 + 1], M[k * 3 + 2]];
  const mix = (w, c) => { col = col.map((v, ch) => v + w * (c[ch] - v)); };
  const A = byteAt(st, j, i, 3), side = A > 0 && h > 0 && st.Many[h] ? 1 : 0;
  const dS = f32(f32(A - A_OFF) / A_SCALE);
  mix(clamp(0.5 + dS, 0.0, 1.0) * side, [M[h * 3], M[h * 3 + 1], M[h * 3 + 2]]);
  mix(st.kerbM * (Math.abs(dS - st.kerbM / 2) < 0.5 && side ? 1 : 0), look.kerb.colour);
  const b6 = byteAt(st, j, i, 6), code = b6 & CMASK, v4 = byteAt(st, j, i, 4);
  if (code === CODE.centre || code === CODE.lane) {
    const dL = f32(f32(v4 - L_OFF) / L_SCALE), dD = f32(f32(byteAt(st, j, i, 5) - D_OFF) / D_SCALE);
    mix(st.lineM * (Math.abs(dL) < 0.5 ? 1 : 0) * clamp(0.5 + dD, 0.0, 1.0), look.lines.colour);
  }
  if (code === CODE.sleepers) {
    const bl = look.ballast, th = (jj, ii) => decG(byteAt(st, jj, ii, 1));
    const d1 = (k0, n, at) => (k0 === 0 ? f32(at(1) - at(0)) : k0 === n - 1 ? f32(at(n - 1) - at(n - 2)) : f32(f32(at(k0 + 1) - at(k0 - 1)) / 2.0));
    const gz = d1(j, st.H1, (q) => th(q, i)), gx = d1(i, st.W1, (q) => th(j, q));
    const o = th(j, i) / Math.max(Math.hypot(gx, gz), 0.02);
    mix((bl.sleeper_w_m / bl.sleeper_pitch_m) * clamp(bl.sleeper_len_m / 2 - Math.abs(o) + 0.5, 0.0, 1.0), bl.colour_sleeper);
  }
  if (code === CODE.treads) {
    const s = look.steps, pitch = f32(f32(s.phase_period_m) / Math.max(2.0 * (b6 >> CODE_BITS), 1.0));
    const fin = clamp(0.5 + f32(f32(v4 - D_OFF) / D_SCALE), 0.0, 1.0);
    const [cn, cr] = stepsEdge(st, [M[k * 3], M[k * 3 + 1], M[k * 3 + 2]]);
    mix(fin * s.nosing_m / pitch, cn);
    mix(fin * 0.5 * s.riser_m / pitch, cr);
  }
  return col;
}

// ---------------------------------------------------------------- pass 3: the ways (_path_pass)
// texel (i, j) of the tile's haloed raster: its colour after the ways (marks
// not yet). rgb = its colour before, hx = hostPass's raster
export function pathTexel(st, T, i, j, rgb, hx) {
  const x = T.xs[i], z = T.zs[j], q = (j * T.PW + i) * 5;
  const look = st.look, nz = look.noise;
  const out = [rgb[0], rgb[1], rgb[2]];
  const fL = hx[q + 4];
  if (T.fine) {
    const tp = taps(st, x, z);
    // dEdge at the texel centre; PATH-G kinds: a wobbled edge, built kinds straight
    const dt = decR(bil(st, tp, 0));
    let d = dt;
    if (T.amp > 0) d = f32(d - f32(f32(T.amp) * f32(2.0 * gnoise(x, z, nz.edge_wl_m, nz.seed) - 1.0)));
    const wt = f32(1.0 - smooth(d, T.ea, T.eb)), wb = f32(1.0 - smooth(dt, T.wa, T.wb));
    // the worn edge band (colour.worn): the host share of the edge ramp, fading out over width_m past it
    const ww = T.fine && T.wn > T.eb ? f32(f32(1 - wt) * f32(1.0 - smooth(d, T.eb, T.wn))) : 0;
    if (!(wt > 0) && !(wb > 0) && !(ww > 0)) return out;
    // signed G, bilinear: |t| = the cross position
    const ts = decG(bil(st, tp, 1)), u = Math.abs(ts);
    let F = null, hw = null;
    for (const [bv, kb] of texelKinds(st, tp)) {
      if (kb !== null && !(kb > 0)) continue;
      if (st.pathKind[bv & KMASK]) {
        const wk = ww > 0 ? look.colour[st.names[bv & KMASK]].worn || 0 : 0;
        if (!(wt > 0) && !(wk > 0)) continue;
        if (hw === null) hw = hwAt(st, T, i, j, ts);
        let cf = cross(st, bv, u, hw);
        if (T.kc < 1.0) {
          const mean = st.crossMean.get(bv);
          for (const p in cf) cf[p] = T.kc * cf[p] + (1.0 - T.kc) * mean[p];
        }
        const w = f32(wt * st.share[bv]);
        const nb = wk > 0 ? f32(ww * f32(st.share[bv] * f32(wk))) : 0;
        addPath(st, T, out, rgb, x, z, bv, kb === null ? w : f32(w * kb), cf, hx, q, kb === null ? nb : f32(nb * kb));
      } else {
        if (!(wb > 0)) continue;
        if (!F) F = features(st, T, i, j, tp, ts);
        let col = colourFine(st, T, bv, F);
        if ((bv & KMASK) === st.kSteps) col = col.map((v) => v * fL);
        const w = kb === null ? wb : f32(wb * kb);
        for (let ch = 0; ch < 3; ch++) out[ch] += w * (col[ch] - rgb[ch]);
      }
    }
    return out;
  }
  if (st.q4) return q4Texel(st, T, i, j, rgb, hx, q, fL);
  // exact area coverage of the cells under the texel, per look byte; the
  // cross-section at its mean across the width. Built kinds: the coverage-
  // weighted cell albedo, the steps' apart (x fL)
  for (const [bv, w] of coarseBlend(st, T, i, j)) {
    if (w > 0) addPath(st, T, out, rgb, x, z, bv, w, st.crossMean.get(bv), hx, q);
  }
  const [bx0, bx1] = block(x, T.texel, st.W1), [bz0, bz1] = block(z, T.texel, st.H1);
  let aw = 0;
  const ac = [0, 0, 0], as = [0, 0, 0];
  for (let cz = bz0; cz < bz1; cz++) {
    for (let cx = bx0; cx < bx1; cx++) {
      const k = byteAt(st, cz, cx, 2) & KMASK;
      if (k === 0 || st.pathKind[k]) continue;
      const cov = f32(clamp(0.5 - decR(byteAt(st, cz, cx, 0)), 0.0, 1.0));
      if (!(cov > 0)) continue;
      const cc = wayCell(st, cz, cx), acc = k === st.kSteps ? as : ac;
      aw += cov;
      for (let ch = 0; ch < 3; ch++) acc[ch] += f32(cov * f32(cc[ch]));
    }
  }
  const fw = f32(aw / T.area);
  for (let ch = 0; ch < 3; ch++) out[ch] += f32(ac[ch] / T.area) - fw * rgb[ch] + f32(as[ch] / T.area) * fL;
  return out;
}

// ---------------------------------------------------------------- the ground marks (_mark_pass / _mark_fc)
// the marks' paint at a sample (xq, zq) as an affine blend out (1 - F) + C ->
// [F, C r g b], or null where it holds no mark (the four taps one code byte)
function markFc(st, xq, zq, texel) {
  const tp = taps(st, xq, zq);
  if (!sameTaps(st, tp, 6)) return null;
  const b0 = byteAt(st, tp.j0, tp.i0, 6), code = b0 & CMASK;
  if (code < MARK0) return null;
  const g = st.ground, hi = b0 >> CODE_BITS;
  const v4 = bil(st, tp, 4), v5 = bil(st, tp, 5), v7 = bil(st, tp, 7);
  const tex = f32(texel);
  let F = 0;
  const C = [0, 0, 0];
  const add = (w, c) => { F += w; for (let ch = 0; ch < 3; ch++) C[ch] += w * c[ch]; };
  const Z = g.zebra, X = g.give_way, R = g.grid;
  const PZ = Z.bar_m + Z.gap_m, PX = X.dash_m + X.gap_m;
  // zebra / grid: the slot run, K = 2 hi slots per turn, the run at m .. m + n - 1
  const KS = Math.max(2.0 * hi, 1.0);
  const m0 = code === CODE.grid ? R.margin_slots : Z.margin_slots;
  const n = KS - 2.0 * m0 - 1.0;
  const phi = npMod(f32(f32(f32(Math.atan2(f32(v7 - P_OFF), f32(v5 - P_OFF))) / (2.0 * Math.PI)) * KS), KS);
  const k = Math.floor(phi), fr = phi - k;
  const rect = clamp(0.5 + f32(f32(v4 - D_OFF) / D_SCALE) / tex, 0.0, 1.0);
  // (slot coverage, bar coverage) of the texel box; the bars faded to their mean when `fade`
  const runBars = (P, bar, fade) => {
    const x = fr * P, c0 = (P - bar) / 2.0, c1 = (P + bar) / 2.0;
    const sc = boxCov(phi * P, m0 * P, (m0 + n) * P, tex);
    let bc = 0;
    for (const jj of [-1.0, 0.0, 1.0]) {
      if (k + jj >= m0 && k + jj <= m0 + n - 1.0) bc += boxCov(x - jj * P, c0, c1, tex);
    }
    const ka = fade ? octaveKeep(P, texel) : 1.0;
    return [sc, ka * bc + (1 - ka) * sc * (bar / P)];
  };
  if (code === CODE.zebra) add(runBars(PZ, Z.bar_m, false)[1] * rect, Z.colour);
  if (code === CODE.grid) {
    const [sc, bc] = runBars(R.pitch_m, R.bar_m, true);
    F += rect * sc;
    for (let ch = 0; ch < 3; ch++) C[ch] += rect * ((sc - bc) * R.pit[ch] + bc * R.colour[ch]);
  }
  if (code === CODE.xline) {
    const a = f32(f32(v4 - L_OFF) / L_SCALE), xs = g.xline_o_scale;
    const dn = Math.min(f32(v5 - D_OFF) / xs, f32(v7 - D_OFF) / xs);
    const inside = clamp(0.5 + dn / tex, 0.0, 1.0);
    for (const [sub, name] of XLINE_SUB) {
      // a line carrying its row of teeth draws the teeth only
      if (hi !== sub) continue;
      const lw = g[name].line_m;
      let w = boxCov(a, -lw / 2, lw / 2, tex) * inside;
      if (name === 'give_way') {
        // dashes from the nearer bound
        const x = npMod(Math.max(dn, 0.0), PX);
        w *= boxCov(x, 0.0, X.dash_m, tex) + boxCov(x - PX, 0.0, X.dash_m, tex);
      }
      add(w, g[name].colour);
    }
    if (hi > TEETH_HB0) {
      // the row of teeth over the line's paint
      const T = g.give_way_tri, bw = T.base_m, ln = T.len_m, gp = T.gap_m, P = bw + gp;
      const nt = Math.max(hi - TEETH_HB0, 1.0);
      const u1 = X.line_m / 2.0 + T.line_gap_m + ln;
      const x = 0.5 * (f32(v5 - D_OFF) / xs - f32(v7 - D_OFF) / xs + nt * P - gp);
      const kt = clamp(Math.floor((x + gp / 2.0) / P), 0.0, nt - 1.0);
      const cx = Math.abs(x - kt * P - bw / 2.0);
      const hw = (bw / 2.0) * clamp((u1 - a) / ln, 0.0, 1.0);
      const w = boxCov(a, u1 - ln, u1, tex) * clamp(0.5 + (hw - cx) / tex, 0.0, 1.0);
      F = F * (1.0 - w) + w;
      for (let ch = 0; ch < 3; ch++) C[ch] = C[ch] * (1.0 - w) + w * T.colour[ch];
    }
  }
  return [F, C[0], C[1], C[2]];
}
// texel (i, j): its colour (after pathTexel) with the ground marks over it.
// Fine: the texel's own sample; coarse: the cells' S x S means box-summed
// over the texel (state.markCells)
export function markTexel(st, T, i, j, rgb) {
  if (!st.markCells.size) return rgb;
  let F, C;
  if (T.fine) {
    const fc = markFc(st, T.xs[i], T.zs[j], T.texel);
    if (!fc) return rgb;
    [F, ...C] = fc;
  } else {
    const [bx0, bx1] = block(T.xs[i], T.texel, st.W1), [bz0, bz1] = block(T.zs[j], T.texel, st.H1);
    let sF = 0;
    const sC = [0, 0, 0];
    for (let cz = bz0; cz < bz1; cz++) {
      for (let cx = bx0; cx < bx1; cx++) {
        const mc = st.markCells.get(cz * st.W1 + cx);
        if (!mc) continue;
        sF += mc[0]; sC[0] += mc[1]; sC[1] += mc[2]; sC[2] += mc[3];
      }
    }
    F = f32(sF / T.area);
    C = sC.map((v) => f32(v / T.area));
  }
  return [rgb[0] * (1.0 - F) + C[0], rgb[1] * (1.0 - F) + C[1], rgb[2] * (1.0 - F) + C[2]];
}

// ---------------------------------------------------------------- the CPU reference's raster form
export function waysPassRaster(st, x0, z0, w_m, h_m, pw, ph, out, cmAt, elevAt) {
  const T = waysTile(st, x0, z0, w_m, h_m, pw, ph);
  if (!T) return out;
  const host = Float32Array.from(out);
  const hx = hostPass(st, T, host, cmAt, elevAt);
  for (let j = 0; j < T.PH; j++) {
    for (let i = 0; i < T.PW; i++) {
      const o = (j * T.PW + i) * 3;
      const c = markTexel(st, T, i, j, pathTexel(st, T, i, j, host.subarray(o, o + 3), hx));
      out[o] = c[0]; out[o + 1] = c[1]; out[o + 2] = c[2];
    }
  }
  return out;
}
